import {
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    mat4,
    vec2,
    vec3,
} from 'gl-matrix';
import {
    domainEventBus,
    StructureSetClosedEvent,
    WindowLevelSetEvent,
} from '~/domain/events';
import type { EventSource } from '~/domain/events/EventTypes';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import { enqueueSnackbar } from '~/utils/notifications';
import { redactDicomUids } from '~/utils/phiRedaction';
import { notifySelectionRefusal } from '~/utils/notifySelectionRefusal';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { WindowLevel } from '~/domain/patient/windowLevel';
import type { AdjacentSliceGhostDepths } from '~/engine/core/render/renderers/slices/adjacentSliceGhost';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    ContourOperations,
    InterpolationPreview,
} from '~/engine/util/algorithms/sdf/ContourOperations';
import { RoiStatistics } from '~/engine/util/algorithms/sdf/ContourStatistics';
import {
    BlendSettings,
    fusionColorMappingById,
    grayscaleFusionColorMapping,
} from '~/domain/viewport/fusionBlend';
import {
    assertValidCheckerboardTileSizeMm,
    DEFAULT_CHECKERBOARD_TILE_SIZE_MM,
} from '~/domain/viewport/checkerboardTileSize';
import { DoseMatrix } from '../../domain/patient/doseMatrix';
import { sharesFrameOfReference } from '../../domain/dicom/uids';
import { Image3D } from '../../domain/patient/image3d';
import type { StructureSetId } from '../../domain/changes/ids';
import { StructureSet } from '../../domain/patient/structureset';
import { ImageViewMode } from '../core/render/renderers/ImageRenderer';
import { PanZoom } from '../view/PanZoom';
import { ViewGeometryLink } from '../view/ViewGeometryLink';
import { ViewManager } from '../view/ViewManager';
import { ViewInvalidatorEventTarget } from '../view/ViewInvalidator';
import { LayerBit } from '../view/layers/LayerBit';
import { alignedImageKey, EnvironmentObjects } from './EnvironmentObjects';
import { PointOfInterest } from './PointOfInterest';
import { EnvironmentObject } from './common/EnvironmentObject';
import { CustomObject } from './common/customs/CustomObject';
import { CustomTargetObject } from './common/customs/CustomTargetObject';
import { DoseDistribution } from './dose/DoseDistribution';
import { ImageObject } from './image/ImageObject';
import { arrivedSlabs, unavailableSlabs } from './image/progressiveSlabs';
import { StructureSetObject } from './structure/StructureSetObject';

/**
 * The event an Environment's host is sent when the Environment starts or
 * stops holding an aligned image.
 */
export const ALIGNED_IMAGES_CHANGED = 'aligned_images_changed';

/**
 * {@link Environment} is a storage class, storing the state of the objects that are currently being viewed.\
 * Environment also stores the common viewing parameters shared between the views.
 */
export class Environment {
    private environmentObjects = new EnvironmentObjects();

    private readonly _panZoom: PanZoom;

    public get panZoom(): PanZoom {
        return this._panZoom;
    }

    /** Which of this environment's views share one scale and centre. */
    public readonly geometryLink = new ViewGeometryLink();

    private readonly _planeIntersectionPoint: PointOfInterest =
        new PointOfInterest();

    public get planeIntersectionPoint(): PointOfInterest {
        return this._planeIntersectionPoint;
    }

    /**
     * Moves the point every view of this environment cuts through, and tells
     * the host.
     */
    public updatePlaneIntersectionPoint(point: ReadonlyVec3): void {
        this._planeIntersectionPoint.coordinates = point;
        this._host.dispatchEvent(new Event('planeIntersectionPointChanged'));
    }

    public get eventTarget(): EventTarget {
        return this._host;
    }

    /**
     * The series a window/level change on this environment applies to: the
     * primary image's.
     */
    public get activeWindowingSeriesUid(): string | undefined {
        return this.image?.parent.seriesUid;
    }

    private _alpha: number = 1.0; // primary image alpha

    public get alpha(): number {
        return this._alpha;
    }

    public set alpha(value: number) {
        this._alpha = value;
    }

    private _viewMode: ImageViewMode = ImageViewMode.AlphaBlending;

    public get viewMode(): ImageViewMode {
        return this._viewMode;
    }

    public set viewMode(mode: ImageViewMode) {
        if (this._viewMode !== mode) {
            this._viewMode = mode;
        }
    }

    /**
     * Whether the checkerboard comparison mode is active.
     *
     * Exposed as a predicate so consumers do not have to import
     * {@link ImageViewMode} from the renderer module. {@link PlaneView}
     * in particular must not: it would close the import cycle
     * `imageAtoms -> ImageRenderer -> PlaneView -> Environment ->
     * imageAtoms`, leaving `ImageViewMode` undefined while
     * `imageAtoms` initialises its atom defaults.
     */
    public get isCheckerboardMode(): boolean {
        return this._viewMode === ImageViewMode.Checkerboard;
    }

    private _checkerboardTileSizeMm = DEFAULT_CHECKERBOARD_TILE_SIZE_MM;

    /** One checkerboard tile's edge on the image plane, in mm. */
    public get checkerboardTileSizeMm(): number {
        return this._checkerboardTileSizeMm;
    }

    private _colorMask: vec3 = grayscaleFusionColorMapping.primaryValues;

    public get colorMask(): vec3 {
        return this._colorMask;
    }

    public set colorMask(value: vec3) {
        this._colorMask = value;
    }

    private _secondaryColorMask: vec3 =
        grayscaleFusionColorMapping.secondaryValues;

    public get secondaryColorMask(): vec3 {
        return this._secondaryColorMask;
    }

    public set secondaryColorMask(value: vec3) {
        this._secondaryColorMask = value;
    }

    private _petFusionColorMapTypeIdx: number = 0;

    public get petFusionColorMapTypeIdx(): number {
        return this._petFusionColorMapTypeIdx;
    }

    public set petFusionColorMapTypeIdx(value: number) {
        this._petFusionColorMapTypeIdx = value;
    }

    /**
     * Structure sets whose switch-away flush threw (see
     * {@link removeStructureSetInternal}): their SDF edits never reached
     * the domain and were lost with the GPU objects. The save workflow
     * must not upload (and report as saved) their stale domain copy.
     * Cleared when the set is activated again — the engine then rebuilds
     * from the domain, so the viewer shows exactly the state a save
     * would persist.
     */
    private readonly flushFailedStructureSetIds = new Set<StructureSetId>();

    /**
     * Flushes the active structure set to the domain data model and
     * reports which sets have previously LOST edits to a failed
     * switch-away flush. A throw from the active set's flush propagates:
     * the caller (the save workflow) must fail loudly, not save stale
     * data.
     */
    public saveChanges(): ReadonlySet<StructureSetId> {
        this.environmentObjects.structureSet?.saveChanges();
        return this.lostEditStructureSetIds();
    }

    /**
     * The structure sets whose edits were lost to a failed switch-away
     * flush and not activated here since, as a snapshot. Reads only: it
     * flushes nothing, so it is safe on a torn-down Environment.
     */
    public lostEditStructureSetIds(): ReadonlySet<StructureSetId> {
        return new Set(this.flushFailedStructureSetIds);
    }

    /**
     * @param _host The {@link ViewManager} that owns this {@link Environment}: it both
     * dispatches this {@link Environment}'s DOM events and takes its repaint requests.
     */
    public constructor(private readonly _host: ViewInvalidatorEventTarget) {
        this._panZoom = new PanZoom(_host);
    }

    public setStructureSet(ss?: StructureSet): EnvironmentObjects | undefined {
        const { structureSet: currentSS, dose: currentDose } =
            this.environmentObjects;

        if (!ss) {
            this.removeStructureSetInternal();
            this._host.invalidate(
                LayerBit.CONTOUR_LAYER_BIT,
                this.environmentObjects.structureSet
            );
            return this.environmentObjects;
        }

        if (!ss.image) {
            notifySelectionRefusal({
                reason: 'structureSetWithoutImage',
                structureSet: ss,
            });
            return;
        }

        if (currentSS?.parent === ss) {
            return this.environmentObjects;
        }

        if (currentSS) {
            this.removeStructureSetInternal();
        }
        if (currentDose) {
            this.removeDoseInternal();
        }

        this.setImage(ss.image);

        const newStructureSet = new StructureSetObject(ss);
        this.environmentObjects.structureSet = newStructureSet;
        // Re-activation rebuilds the engine state from the domain: the
        // viewer now shows exactly what a save would persist, so a
        // previously lost switch-away flush no longer blocks saving.
        this.flushFailedStructureSetIds.delete(ss.id);

        this._host.invalidate(
            LayerBit.CONTOUR_LAYER_BIT,
            this.environmentObjects.structureSet
        );

        for (const roi of newStructureSet.structures) {
            if (roi.renderErrors) {
                enqueueSnackbar(
                    `Error rendering structure ${roi.name}: ${roi.renderErrors}`,
                    { variant: 'error' }
                );
            }
        }

        return this.environmentObjects;
    }

    public setDose(dose: DoseMatrix | undefined) {
        if (this.environmentObjects.dose) {
            if (this.environmentObjects.dose.parent === dose) {
                return this.environmentObjects;
            }
            this.removeDoseInternal();
        }
        if (dose) {
            if (!dose.structureSet) {
                notifySelectionRefusal({ reason: 'doseWithoutStructureSet' });
                return this.environmentObjects;
            }
            if (!dose.structureSet.image) {
                notifySelectionRefusal({ reason: 'doseWithoutImage' });
                return this.environmentObjects;
            }
            const ssResult = this.setStructureSet(dose.structureSet);
            if (!ssResult) {
                return this.environmentObjects;
            }
            this.environmentObjects.dose = new DoseDistribution(dose);
            this.announceDoseDisplayChange();
        }
        this._host.invalidate(
            LayerBit.DOSE_LAYER_BIT,
            this.environmentObjects.dose
        );
        return this.environmentObjects;
    }

    /**
     * Tells the host that the dose this Environment displays changed: a
     * dose was set or removed, or the dose was shown or hidden.
     */
    private announceDoseDisplayChange(): void {
        this._host.dispatchEvent(new Event('dose_display_changed'));
    }

    public updateSelectedDoseRange(range: ReadonlyVec2) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.selectedDoseRange = range;
            this._host.invalidate(
                LayerBit.DOSE_LAYER_BIT,
                this.environmentObjects.dose
            );
        }
    }

    public updateDoseAlphaLevelOpacity(opacity: number) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.alphaLevelOpacity = opacity;
            this._host.invalidate(
                LayerBit.DOSE_LAYER_BIT,
                this.environmentObjects.dose
            );
        }
    }

    public updateDoseVisibility(visible: boolean) {
        if (this.environmentObjects.dose) {
            this.environmentObjects.dose.isVisible = visible;
            this._host.invalidate(
                LayerBit.DOSE_LAYER_BIT,
                this.environmentObjects.dose
            );
            this.announceDoseDisplayChange();
        }
    }

    /**
     * Builds the {@link ImageObject} for an {@link Image3D}, whole or not.
     *
     * A volume that is still filling gets full-size storage and only the rows
     * that have actually arrived; the streaming controller pushes the rest in
     * through {@link uploadStreamedSlice}. A complete volume — including every
     * local-file open and a streamed one that finished before it was picked up
     * — takes the single full upload it always took.
     */
    private static createImageObject(
        image: Image3D,
        transformation?: ReadonlyMat4
    ): ImageObject {
        if (image.isProgressivelyLoaded && !image.isComplete) {
            const imageObject = ImageObject.fromPartialImage3D(
                image,
                image.firstSlice,
                transformation
            );
            for (const slab of arrivedSlabs(image)) {
                imageObject.uploadSlab(slab.zStart, slab.zCount);
            }
            // Rows already written off carry no pixels and never will, so they
            // are classified here too — an image picked up after retrieval gave
            // up must not show those rows as merely still loading.
            for (const slab of unavailableSlabs(image)) {
                imageObject.markSlabUnavailable(slab.zStart, slab.zCount);
            }
            return imageObject;
        }
        return ImageObject.fromImage3D(image, image.firstSlice, transformation);
    }

    /**
     * Shows one just-arrived slice of a progressively loaded volume, with
     * two effects:
     *
     * 1. The slice is pushed into the GPU texture of every slot that draws
     *    the volume, and the image layer is repainted.
     * 2. When the slice completed the volume, the window of the completed
     *    load is published: see {@link publishWindowOfCompletedLoad}.
     *
     * The publish comes last, so a window subscriber that throws leaves the
     * slice uploaded and the repaint requested. Its error is not caught
     * here: it reaches the caller.
     *
     * A no-op when the volume is not on screen: its pixels are already in the
     * {@link Image3D}, and {@link createImageObject} uploads everything that
     * has arrived if and when the image is displayed.
     *
     * @param image The volume the slice was inserted into.
     * @param zIndex The z row {@link Image3D.insertSlice} wrote, never a
     * caller's own arithmetic.
     */
    public uploadStreamedSlice(image: Image3D, zIndex: number): void {
        const targets = this.imageObjects();
        let isDrawn = false;
        for (const target of targets) {
            if (target.parent !== image) {
                continue;
            }
            target.uploadSlab(zIndex, 1);
            // Per-frame coalescing lives in the invalidator, so one call per
            // slice costs one repaint per frame, not one per slice.
            this._host.invalidate(LayerBit.IMAGE_LAYER_BIT, target);
            isDrawn = true;
        }
        if (isDrawn && image.isComplete) {
            this.publishWindowOfCompletedLoad(image.seriesUid);
        }
    }

    /**
     * Asks the volume of a series this Environment draws for its
     * first-display window again and, when that differs from the window
     * the volume holds, publishes it as a window set, so every view and
     * readout of the series follows.
     *
     * The answer differs only for a PET whose load has completed while it
     * holds a window on the placeholder level bounds of the load (see
     * {@link Image3D.windowLevelForFirstDisplay}): the measured full range
     * is published for a window the user did not choose, or the held width
     * and center on the measured level bounds for one the user chose. For
     * any other volume, a volume still loading and a series not drawn
     * here, nothing is published.
     *
     * @param seriesUid The DICOM Series Instance UID of the volume to ask.
     */
    public publishWindowOfCompletedLoad(seriesUid: string): void {
        const image = this.findImageBySeriesUid(seriesUid)?.parent;
        if (image === undefined) {
            return;
        }
        const level = image.windowLevelForFirstDisplay();
        if (image.windowLevel?.equals(level) === false) {
            domainEventBus.publishSync(
                new WindowLevelSetEvent(level, seriesUid, 'engine')
            );
        }
    }

    /**
     * Marks one row of a progressively loaded volume as never arriving and
     * repaints the image layer, so it is drawn as absent rather than as the
     * zero it holds.
     *
     * A no-op when the volume is not on screen: {@link createImageObject}
     * classifies every written-off row if and when the image is displayed.
     *
     * @param image The volume whose slice was written off.
     * @param zIndex The z row the slice would have occupied, never a caller's
     * own arithmetic.
     */
    public markStreamedSliceUnavailable(image: Image3D, zIndex: number): void {
        for (const target of this.imageObjects()) {
            if (target.parent !== image) {
                continue;
            }
            target.markSlabUnavailable(zIndex, 1);
            this._host.invalidate(LayerBit.IMAGE_LAYER_BIT, target);
        }
    }

    public setImage(image: Image3D | undefined) {
        if (this.environmentObjects.primaryImage) {
            if (this.environmentObjects.primaryImage.parent === image) {
                return;
            }
            this.removeImagesInternal();
        }
        if (this.environmentObjects.dose) {
            this.removeDoseInternal();
        }
        if (this.environmentObjects.structureSet) {
            this.removeStructureSetInternal();
        }
        if (image) {
            this.environmentObjects = new EnvironmentObjects();
            this.environmentObjects.primaryImage =
                Environment.createImageObject(image);
            ViewManager.resetViewsForEnvironment(this);
        } else {
            this._host.invalidate(
                LayerBit.ALL_LAYERS,
                this.environmentObjects.primaryImage
            );
        }
        return this.environmentObjects;
    }

    /**
     * Sets the registration slot and re-transforms the fused secondary
     * image, if any, so the views show the new alignment at once. The
     * transform is a no-op until both images are present.
     */
    public setRegistration(
        registration: RigidRegistration | undefined
    ): EnvironmentObjects {
        this.environmentObjects.registration = registration;
        this.updateSecondaryTransformation();
        this._host.dispatchEvent(new Event('registration_changed'));
        return this.environmentObjects;
    }

    /**
     * Writes a new matrix into the current registration and re-transforms
     * the fused secondary image, here and in every other Environment whose
     * fusion the same registration backs. An approved registration is
     * immutable, so the write is refused without an event.
     */
    public setRegistrationMatrix(newMatrix: mat4) {
        if (!this.registration || this.registration.isApproved) {
            return;
        }
        mat4.copy(this.registration.transformation, newMatrix);
        this.updateSecondaryTransformation();
        ViewManager.retransformFusionsSharing(this.registration, this);
        this._host.dispatchEvent(new Event('registration_changed'));
    }

    /**
     * {@link CustomObject}s drawn on every view showing this environment,
     * over its layers, for as long as they are listed: the in-progress
     * state of an operation, shown wherever the image is displayed. Publish
     * channel only — the environment does not own the objects; whoever
     * adds one removes it and releases it.
     */
    private readonly _customObjects: CustomObject[] = [];

    public get customObjects(): readonly CustomObject[] {
        return this._customObjects;
    }

    public addCustomObject(obj: CustomObject): void {
        this._customObjects.push(obj);
    }

    /** Stop drawing `obj`; a no-op when it is not listed. */
    public removeCustomObject(obj: CustomObject): void {
        const index = this._customObjects.indexOf(obj);
        if (index >= 0) {
            this._customObjects.splice(index, 1);
        }
    }

    private _operationPreviewStructure?: CustomTargetObject;
    private readonly _operationPreviewListeners = new Set<() => void>();
    /**
     * Publish channel only — the slot does not own its value. Notifies the
     * {@link subscribeToOperationPreview} listeners when a different preview,
     * or none, is on screen.
     */
    public set operationPreviewStructure(
        preview: CustomTargetObject | undefined
    ) {
        if (preview === this._operationPreviewStructure) {
            return;
        }
        this._operationPreviewStructure = preview;
        this._operationPreviewListeners.forEach((listener) => listener());
    }

    /**
     * Call `listener` whenever {@link operationPreviewStructure} changes.
     * Held by the environment itself, so it survives the host moving to
     * another element. Returns the unsubscribe function.
     */
    public subscribeToOperationPreview(listener: () => void): () => void {
        this._operationPreviewListeners.add(listener);
        return () => {
            this._operationPreviewListeners.delete(listener);
        };
    }

    public get operationPreviewStructure(): CustomTargetObject | undefined {
        return this._operationPreviewStructure;
    }

    private _structureIsolationActive = false;
    /**
     * Soft-isolate display mode (see structureIsolationActiveState): dims
     * all but the selected structure, display-only. Publish channel only —
     * the React layer owns the value.
     */
    public set structureIsolationActive(active: boolean) {
        this._structureIsolationActive = active;
    }

    public get structureIsolationActive(): boolean {
        return this._structureIsolationActive;
    }

    /**
     * Peek (see activeStructurePeekState): the ROI number whose contour the
     * slice views leave out for as long as it is also the selected
     * structure, or null. Display-only. Publish channel only — the React
     * layer owns the value.
     */
    public peekedRoiNumber: number | null = null;

    public changeFusionColorMapping(
        primaryValues: vec3,
        secondaryValues: vec3
    ) {
        this.colorMask = primaryValues;
        this.secondaryColorMask = secondaryValues;

        this._host.invalidate(LayerBit.IMAGE_LAYER_BIT, this.image);
    }

    public changePetFusionColorMapping(colorMapTypeIdx: number) {
        if (!this.image) {
            return;
        }
        this.petFusionColorMapTypeIdx = colorMapTypeIdx;
        this._host.invalidate(LayerBit.IMAGE_LAYER_BIT, this.image);
    }

    /**
     * Applies the fields present in `settings` to how the fused pair is
     * drawn: the mix, the view mode, the checkerboard tile, and the
     * colours, each resolved from its id. The one mapping from a blend
     * record to this Environment; a field left out is left as it is.
     */
    public applyBlendSettings(settings: Partial<BlendSettings>): void {
        if (settings.alpha !== undefined) {
            this.updatePrimaryImageAlpha(settings.alpha);
        }
        if (settings.mode !== undefined) {
            this.updateImageViewMode(
                settings.mode === 'checkerboard'
                    ? ImageViewMode.Checkerboard
                    : ImageViewMode.AlphaBlending
            );
        }
        if (settings.checkerboardTileSizeMm !== undefined) {
            this.updateCheckerboardTileSize(settings.checkerboardTileSizeMm);
        }
        if (settings.colorMappingId !== undefined) {
            const mapping = fusionColorMappingById(settings.colorMappingId);
            this.changeFusionColorMapping(
                mapping.primaryValues,
                mapping.secondaryValues
            );
        }
        if (settings.petColorMapId !== undefined) {
            this.changePetFusionColorMapping(settings.petColorMapId);
        }
    }

    private removeImagesInternal() {
        for (const slot of this.environmentObjects.alignedImages.values()) {
            this.removeObject(slot.object);
        }
        this.environmentObjects.alignedImages.clear();
        if (this.environmentObjects.secondaryImage) {
            this.removeObject(this.environmentObjects.secondaryImage);
            this.environmentObjects.secondaryImage = undefined;
        }
        if (this.environmentObjects.primaryImage) {
            this.removeObject(this.environmentObjects.primaryImage);
            this.environmentObjects.primaryImage = undefined;
        }
    }

    private removeEnvironmentSecondaryImageInternal() {
        if (this.environmentObjects.secondaryImage) {
            this.removeObject(this.environmentObjects.secondaryImage);
            this.environmentObjects.secondaryImage = undefined;
        }
    }

    private removeDoseInternal() {
        if (this.environmentObjects.dose) {
            this.removeObject(this.environmentObjects.dose);
            this.environmentObjects.dose = undefined;
            this.announceDoseDisplayChange();
        }
    }

    private removeStructureSetInternal() {
        const ss = this.environmentObjects.structureSet;
        if (!ss) {
            return;
        }
        // Persistence belongs to an explicit boundary flush BEFORE
        // teardown reaches here; this save is only a backstop.
        const unsavedRois = ss.structures.filter(
            (roi) => !roi.isPointRoi && roi.hasBeenModified
        );
        if (unsavedRois.length > 0) {
            console.error(
                `A structure set reached teardown with ${unsavedRois.length} ` +
                    'unsaved ROI(s); an explicit flush was missed. ' +
                    'Attempting backstop save.'
            );
        }
        try {
            ss.saveChanges();
        } catch (e) {
            console.error(
                'Backstop save failed for a structure set; unsaved contour ' +
                    'edits may be lost.',
                redactDicomUids(e)
            );
            // The un-flushed edits are lost with the GPU objects below.
            // Record it STICKILY so a later save fails this set instead of
            // persisting its stale domain copy as a success; re-activating
            // the set clears the mark (the viewer then shows the true,
            // reverted state).
            this.flushFailedStructureSetIds.add(ss.parent.id);
            enqueueSnackbar(
                `Error saving structure set changes: ${ss.displayName}, ${e}.`,
                { variant: 'error' }
            );
        }
        this.removeObject(ss);
        this.setSelectedStructure(undefined);
        this.environmentObjects.structureSet = undefined;
        void domainEventBus
            .publish(new StructureSetClosedEvent(ss.parent.dicomUid))
            .catch((error) =>
                console.error(
                    'Failed to publish StructureSetClosedEvent:',
                    redactDicomUids(error)
                )
            );
    }

    public clearContourOnSlice(roiNumber: number, plane: PlaneMm) {
        if (this.environmentObjects.structureSet) {
            const imgObject = this.environmentObjects.primaryImage;
            this.environmentObjects.structureSet.clearContoursOnSlice(
                roiNumber,
                plane,
                imgObject
            );
            this._host.invalidate(
                LayerBit.CONTOUR_LAYER_BIT,
                this.environmentObjects.structureSet
            );
        }
    }

    public clearAllContours(roiNumber: number) {
        if (this.environmentObjects.structureSet) {
            this.environmentObjects.structureSet.clearAllContours(roiNumber);
            this._host.invalidate(
                LayerBit.CONTOUR_LAYER_BIT,
                this.environmentObjects.structureSet
            );
        }
    }

    public keepEveryNthSlice(
        roiNumber: number,
        keepEveryNthSliceValue: number
    ) {
        if (this.environmentObjects.structureSet) {
            this.environmentObjects.structureSet.keepEveryNthSlice(
                roiNumber,
                keepEveryNthSliceValue
            );
            this._host.invalidate(
                LayerBit.CONTOUR_LAYER_BIT,
                this.environmentObjects.structureSet
            );
        }
    }

    /**
     * The one way non-engine code asks the views to repaint the contour
     * layer. Application code must not reach for `ViewManager.invalidate`
     * itself: that static is a transitional shim, and this environment
     * already holds the invalidator that owns its views.
     *
     * Always repaints, even with no structure set: the contour layer also
     * draws things the set does not own (the operation preview, comparison
     * overlays added straight to the views), so "no set" does not mean
     * "nothing to repaint". Without a set there is no region to bound the
     * repaint to, so every view is repainted — the cost of not knowing,
     * never a missed frame.
     */
    public triggerContourLayerUpdate() {
        this._host.invalidate(
            LayerBit.CONTOUR_LAYER_BIT,
            this.environmentObjects.structureSet
        );
    }

    private _crossSliceDiffEnabled = false;

    /**
     * Whether the cross-slice diff overlay (§5.9.4) is active. Read by
     * {@link TextureView.renderLayers} to fill the selected ROI's
     * added/removed regions versus the previous acquisition slice.
     * Display aid only — never changes contour geometry.
     */
    public get crossSliceDiffEnabled(): boolean {
        return this._crossSliceDiffEnabled;
    }

    /**
     * Toggle the cross-slice diff overlay and repaint the contour
     * layer so the overlay appears/disappears immediately.
     */
    public updateCrossSliceDiff(enabled: boolean) {
        this._crossSliceDiffEnabled = enabled;
        this.triggerContourLayerUpdate();
    }

    private _adjacentSliceGhosts: AdjacentSliceGhostDepths | null = null;

    /** Which slices the adjacent-slice ghosts show on each side, or null while off; read by {@link ContourLayer}. */
    public get adjacentSliceGhosts(): AdjacentSliceGhostDepths | null {
        return this._adjacentSliceGhosts;
    }

    /** Set which slices the adjacent-slice ghosts show (null: off) and repaint the contours. */
    public updateAdjacentSliceGhosts(depths: AdjacentSliceGhostDepths | null) {
        this._adjacentSliceGhosts = depths;
        this.triggerContourLayerUpdate();
    }

    public setSecondaryImage(
        image: Image3D | undefined,
        registration?: RigidRegistration
    ) {
        if (!this.environmentObjects.primaryImage) {
            return;
        }
        const hadSecondaryImage = Boolean(
            this.environmentObjects.secondaryImage
        );
        if (hadSecondaryImage) {
            this.removeEnvironmentSecondaryImageInternal();
        }

        if (image) {
            if (
                !this.canBePickedAsSecondary(
                    this.environmentObjects.primaryImage.parent,
                    image,
                    registration
                )
            ) {
                return;
            }
            const transformation = registration?.getTransformFromTo(
                image.frameOfReferenceUid,
                this.environmentObjects.primaryImage.parent.frameOfReferenceUid
            );

            // Only throw error if registration was provided but transformation failed
            if (!transformation && registration) {
                throw new Error(
                    'Could not construct transformation matrix for secondary image.'
                );
            }
            this.environmentObjects.secondaryImage =
                Environment.createImageObject(image, transformation);
            // A secondary-image bind resets only its own Environment's
            // views, never every resident Environment's cameras.
            ViewManager.resetViewsForEnvironment(this);
        } else if (hadSecondaryImage) {
            // Removing the fused image returns the views to their
            // anatomical planes; the zoom and the crosshair stay as they
            // are.
            ViewManager.clearFrameRotation(this);
        }
        this._host.dispatchEvent(new Event('image_changed'));
        return this.environmentObjects;
    }

    /**
     * Applies a secondary image as a fused pair: drops any outgoing
     * secondary, then sets the registration slot before the new secondary
     * image (the transform `setSecondaryImage` computes depends on it). A
     * rejected pair leaves the registration slot as it was. How the pair
     * is drawn together is not touched here; that reaches the Environment
     * through {@link applyBlendSettings}.
     */
    public fuseSecondaryImage(
        image: Image3D,
        registration?: RigidRegistration
    ): EnvironmentObjects | undefined {
        const previousRegistration = this.registration;
        // The slot write re-transforms whatever secondary is resident; the
        // outgoing image must not be re-gridded against the new pair's
        // registration.
        this.removeEnvironmentSecondaryImageInternal();
        this.setRegistration(registration);
        const result = this.setSecondaryImage(image, registration);
        if (!result) {
            this.setRegistration(previousRegistration);
            return undefined;
        }
        return result;
    }

    /**
     * Removes the secondary image and clears the registration. The blend
     * settings stay as they are: the renderer draws a lone primary opaque
     * whatever the mix says.
     */
    public clearSecondaryFusion(): EnvironmentObjects | undefined {
        const result = this.setSecondaryImage(undefined);
        this.setRegistration(undefined);
        return result;
    }

    /**
     * Places every registered image of this Environment where its
     * registration now puts it: the fused secondary and each aligned image.
     */
    public updateSecondaryTransformation(): void {
        this.updateAlignedTransformations();
        const primaryImage = this.environmentObjects.primaryImage?.parent;
        const secondaryImageObj = this.environmentObjects.secondaryImage;
        const registration = this.registration;

        if (!primaryImage || !secondaryImageObj || !registration) {
            // No-op when registration is absent (same-FOR images
            // don't need a transformation) or when images are not
            // loaded yet.
            return;
        }

        const newTransformationMatrix = registration?.getTransformFromTo(
            secondaryImageObj.parent.frameOfReferenceUid,
            primaryImage.frameOfReferenceUid
        );

        if (!newTransformationMatrix) {
            console.error('Could not construct transformation matrix.');
            return;
        }

        secondaryImageObj.updateGridTransformation(newTransformationMatrix);

        this._host.invalidate(
            LayerBit.IMAGE_LAYER_BIT,
            this.environmentObjects.secondaryImage
        );

        this._host.dispatchEvent(new Event('image_changed'));
    }

    /**
     * Holds `image` placed in the primary's frame through `registration`
     * (none for a shared frame of reference), for a view that shows it
     * alone, and returns the key the view reads it by. A volume already
     * held through the same registration is shared, not uploaded again.
     * Undefined when there is no primary image or the pair is not linked:
     * the same rule as a fusion, so an unregistered image is never placed.
     */
    public retainAlignedImage(
        image: Image3D,
        registration?: RigidRegistration
    ): string | undefined {
        const primary = this.environmentObjects.primaryImage?.parent;
        if (
            !primary ||
            !this.canBePickedAsSecondary(primary, image, registration)
        ) {
            return undefined;
        }
        const key = alignedImageKey(image.seriesUid, registration?.id);
        const held = this.environmentObjects.alignedImages.get(key);
        if (held) {
            held.holders += 1;
            return key;
        }
        const transformation = registration?.getTransformFromTo(
            image.frameOfReferenceUid,
            primary.frameOfReferenceUid
        );
        if (registration && !transformation) {
            return undefined;
        }
        this.environmentObjects.alignedImages.set(key, {
            object: Environment.createImageObject(image, transformation),
            registration,
            holders: 1,
        });
        this._host.dispatchEvent(new Event(ALIGNED_IMAGES_CHANGED));
        return key;
    }

    /**
     * Gives back one hold on the aligned image `key`; the last frees it. A
     * key not held, or held by an image since torn down, is a no-op.
     */
    public releaseAlignedImage(key: string): void {
        const held = this.environmentObjects.alignedImages.get(key);
        if (!held) {
            return;
        }
        held.holders -= 1;
        if (held.holders > 0) {
            return;
        }
        this.environmentObjects.alignedImages.delete(key);
        this.removeObject(held.object);
        this._host.dispatchEvent(new Event(ALIGNED_IMAGES_CHANGED));
    }

    /** The aligned image held under `key`, or undefined when none is. */
    public alignedImage(key: string): ImageObject | undefined {
        return this.environmentObjects.alignedImages.get(key)?.object;
    }

    /** The registration that places the aligned image `key`; undefined for a shared frame or a key not held. */
    public alignedImageRegistration(
        key: string
    ): RigidRegistration | undefined {
        return this.environmentObjects.alignedImages.get(key)?.registration;
    }

    /** The volumes of every aligned image held, each once. */
    public alignedVolumes(): Image3D[] {
        return [
            ...new Set(
                [...this.environmentObjects.alignedImages.values()].map(
                    (slot) => slot.object.parent
                )
            ),
        ];
    }

    /** Whether an aligned image of this Environment is placed through `registration`. */
    public alignsThrough(registration: RigidRegistration): boolean {
        for (const slot of this.environmentObjects.alignedImages.values()) {
            if (slot.registration === registration) {
                return true;
            }
        }
        return false;
    }

    /** Re-places each registered aligned image where its registration now puts it. */
    private updateAlignedTransformations(): void {
        const primary = this.environmentObjects.primaryImage?.parent;
        if (!primary) {
            return;
        }
        for (const slot of this.environmentObjects.alignedImages.values()) {
            if (!slot.registration) {
                continue;
            }
            const transformation = slot.registration.getTransformFromTo(
                slot.object.parent.frameOfReferenceUid,
                primary.frameOfReferenceUid
            );
            if (!transformation) {
                console.error('Could not construct transformation matrix.');
                continue;
            }
            slot.object.updateGridTransformation(transformation);
            this._host.invalidate(LayerBit.IMAGE_LAYER_BIT, slot.object);
        }
    }

    /** Every image object this Environment draws from: primary, secondary and aligned. */
    private imageObjects(): ImageObject[] {
        const { primaryImage, secondaryImage, alignedImages } =
            this.environmentObjects;
        return [
            ...(primaryImage ? [primaryImage] : []),
            ...(secondaryImage ? [secondaryImage] : []),
            ...[...alignedImages.values()].map((slot) => slot.object),
        ];
    }

    public updatePrimaryImageAlpha(alpha: number) {
        if (!this.image) {
            return;
        }
        if (alpha >= 0 && alpha <= 1) {
            this.alpha = alpha;
            this._host.invalidate(LayerBit.IMAGE_LAYER_BIT, this.image);
        }
    }

    /**
     * Sets the checkerboard tile edge, in mm on the image plane. Throws on
     * a size that is not a positive length; nothing is clamped.
     */
    public updateCheckerboardTileSize(sizeMm: number) {
        assertValidCheckerboardTileSizeMm(sizeMm);
        if (!this.image) {
            return;
        }
        this._checkerboardTileSizeMm = sizeMm;
        this._host.invalidate(LayerBit.IMAGE_LAYER_BIT, this.image);
    }

    public updateImageViewMode(mode: ImageViewMode) {
        // Kept with no image shown, so the next image draws in the mode the
        // controls show.
        this.viewMode = mode;
        if (!this.image) {
            return;
        }
        this._host.invalidate(LayerBit.IMAGE_LAYER_BIT, this.image);
    }

    /**
     * Determines whether `image2` can be used as a secondary (fusion) image
     * for `image1`.
     *
     * The rules are:
     * - Same Frame of Reference → always allowed.
     * - Otherwise a registration that links the two FORs must exist,
     *   whatever the modalities or the study of the two images.
     */
    private canBePickedAsSecondary(
        image1: Image3D,
        image2: Image3D,
        registration?: RigidRegistration
    ): boolean {
        if (
            sharesFrameOfReference(
                image1.frameOfReferenceUid,
                image2.frameOfReferenceUid
            )
        ) {
            return true;
        }
        if (!registration) {
            return false;
        }
        // getTransformFromTo returns undefined when the FORs are unrelated
        const transform = registration.getTransformFromTo(
            image2.frameOfReferenceUid,
            image1.frameOfReferenceUid
        );
        return transform !== undefined;
    }

    private removeObject(obj: EnvironmentObject) {
        obj.delete();
    }

    public get structureSet(): StructureSetObject | undefined {
        return this.environmentObjects.structureSet;
    }

    public get dose(): DoseDistribution | undefined {
        return this.environmentObjects.dose;
    }
    public get image(): ImageObject | undefined {
        return this.environmentObjects.primaryImage;
    }

    public get registration(): RigidRegistration | undefined {
        return this.environmentObjects.registration;
    }

    public get secondaryImage(): ImageObject | undefined {
        return this.environmentObjects.secondaryImage;
    }

    /** Update the selected structure and update views */
    public setSelectedStructure(selectedROI: RegionOfInterest | undefined) {
        if (!this.environmentObjects.structureSet) {
            return;
        }
        if (
            this.environmentObjects.structureSet.selectedStructure ===
            selectedROI
        ) {
            return;
        }
        this.environmentObjects.structureSet.selectedStructure = selectedROI;
        this._host.invalidate(
            LayerBit.CONTOUR_LAYER_BIT,
            this.environmentObjects.structureSet
        );
    }

    public setSelectedRoiByNumber(roiNumber: number) {
        if (!this.environmentObjects.structureSet) {
            return;
        }
        if (roiNumber === -1) {
            this.setSelectedStructure(undefined);
            return;
        }
        const roi = this.environmentObjects.structureSet.getRoi(roiNumber);
        if (roi) {
            this.setSelectedStructure(roi);
        }
    }

    public getRoi(roiNumber: number): RegionOfInterest | undefined {
        return this.environmentObjects.structureSet?.getRoi(roiNumber);
    }

    public getRoiVolumeCm3(roiNumber: number): number {
        return (
            this.environmentObjects.structureSet?.getRoi(roiNumber)
                ?.volumeCm3 ?? Number.NaN
        );
    }

    /**
     * SDF-derived shape statistics (surface area, sphericity, center of mass,
     * bounding box) for an ROI, or undefined when unavailable (point ROI,
     * empty, or no structure set).
     */
    public getRoiStatistics(roiNumber: number): RoiStatistics | undefined {
        return this.environmentObjects.structureSet?.getRoi(roiNumber)
            ?.statistics;
    }

    public isRoiEmpty(roiNumber: number): boolean {
        return (
            this.environmentObjects.structureSet?.getRoi(roiNumber)?.isEmpty ??
            true
        );
    }

    /**
     * Sets the window level of an image identified by its DICOM Series Instance UID.
     * @param level - The new window level values
     * @param seriesUid - The DICOM Series Instance UID of the image to update
     * @param source - Who set it; the image records a window the user chose
     * @returns true when the series resolved to an image hosted by this environment
     */
    public setImageWindowLevel(
        level: WindowLevel,
        seriesUid: string,
        source: EventSource
    ): boolean {
        const image = this.findImageBySeriesUid(seriesUid);
        if (!image) {
            return false;
        }
        this.applyWindowLevel(image, level, source);
        return true;
    }

    /**
     * Adjusts the window level of an image by a delta amount.
     * The stepped window is written with the source 'engine', so it is not
     * recorded as a window the user chose (see {@link Image3D.setWindowLevel}).
     * @param delta - The delta values [windowDelta, levelDelta]
     * @param seriesUid - The DICOM Series Instance UID of the image to update
     * @returns true when the series resolved to an image with a window level in this environment
     */
    public adjustImageWindowLevel(delta: vec2, seriesUid: string): boolean {
        const image = this.findImageBySeriesUid(seriesUid);
        if (!image?.windowLevel) {
            return false;
        }
        this.applyWindowLevel(image, image.windowLevel.add(delta), 'engine');
        return true;
    }

    private applyWindowLevel(
        image: ImageObject | undefined,
        level: WindowLevel,
        source: EventSource
    ): void {
        if (image && image.windowLevel && !image.windowLevel.equals(level)) {
            image.setWindowLevel(level, source);
            // The window belongs to the volume, which other Environments may
            // draw too, fused or aligned, each placed in its own frame: no
            // one box bounds the views it reaches, so none is culled.
            this._host.invalidate(LayerBit.ALL_LAYERS);
        }
    }

    /**
     * Returns the current window level for the image identified
     * by its parent Image3D's seriesUid.
     */
    public getImageWindowLevel(seriesUid: string): WindowLevel | undefined {
        return this.findImageBySeriesUid(seriesUid)?.windowLevel;
    }

    /**
     * Finds an ImageObject by its parent Image3D's seriesUid.
     */
    private findImageBySeriesUid(seriesUid: string): ImageObject | undefined {
        return this.imageObjects().find(
            (image) => image.parent?.seriesUid === seriesUid
        );
    }

    public handleInterpolateSingleSlice(
        roi: RegionOfInterest,
        plane: PlaneMm,
        imageGuided = false
    ) {
        const primaryImage = this.environmentObjects.primaryImage;
        if (!primaryImage) {
            return;
        }
        const contourOperations = new ContourOperations();
        contourOperations.handleInterpolateSingleSlice(
            roi,
            primaryImage,
            plane,
            { enabled: imageGuided }
        );
        this._host.invalidate(
            LayerBit.CONTOUR_LAYER_BIT,
            this.environmentObjects.structureSet
        );
    }

    public handleInterpolateAllSlices(
        roi: RegionOfInterest,
        imageGuided = false
    ) {
        const contourOperations = new ContourOperations();
        contourOperations.handleInterpolateAllSlices(
            roi,
            this.environmentObjects.primaryImage,
            { enabled: imageGuided }
        );
        this._host.invalidate(
            LayerBit.CONTOUR_LAYER_BIT,
            this.environmentObjects.structureSet
        );
    }

    /**
     * Read-only preview of which slices {@link handleInterpolateAllSlices}
     * would fill, for surfacing to the user before they apply.
     */
    public previewInterpolateAllSlices(
        roi: RegionOfInterest
    ): InterpolationPreview {
        return new ContourOperations().previewInterpolateAllSlices(roi);
    }

    /**
     * Read-only preview of whether {@link handleInterpolateSingleSlice} would
     * fill the slice under the viewed plane.
     */
    public previewInterpolateSingleSlice(
        roi: RegionOfInterest,
        plane: PlaneMm
    ): InterpolationPreview {
        const primaryImage = this.environmentObjects.primaryImage;
        if (!primaryImage) {
            return { status: 'unavailable' };
        }
        return new ContourOperations().previewInterpolateSingleSlice(
            roi,
            primaryImage,
            plane
        );
    }

    /**
     * Builds a translucent preview texture of the interpolation result for the
     * given scope, or undefined when there is nothing to fill. The caller owns
     * the returned texture (publish via {@link operationPreviewStructure}; the
     * preview lifecycle disposes it).
     */
    public buildInterpolatedPreviewTexture(
        roi: RegionOfInterest,
        scope: 'allEmpty' | 'current',
        plane: PlaneMm | null,
        imageGuided = false
    ): ContourTexture | undefined {
        const primaryImage = this.environmentObjects.primaryImage;
        if (!primaryImage) {
            return undefined;
        }
        return new ContourOperations().buildInterpolatedPreviewTexture(
            roi,
            scope,
            primaryImage,
            plane,
            { enabled: imageGuided }
        );
    }
}
